import React, { useEffect } from 'react';
import styled from 'styled-components';
import trapFocus from 'lib/trapFocus';

export interface ModalProps {
  isOpen: boolean;
  children: React.ReactNode;
  closeModal: () => void;
  className?: string;
  config?: any;
  disableDismiss?: boolean;
  enableBackgroundScroll?: boolean;
}

interface ModalConfig {
  isOpen?: boolean;
  backdropColour?: string;
}

const ModalBackdrop = styled.div<ModalConfig>`
  display: ${props => (props.theme.isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  position: fixed;
  z-index: 100;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgb(0, 0, 0);
  background-color: ${props =>
    props.theme.backdropColour
      ? props.theme.backdropColour
      : 'rgba(0,0,0,0.4)'};
`;

const ModalContainer = styled.div`
  width: 50%;
  margin: 0 auto;
  background-color: white;
  padding: 2.5rem;
`;

const Modal = ({
  children,
  className,
  enableBackgroundScroll = false,
  isOpen,
  config,
  closeModal,
  disableDismiss = false,
}: ModalProps) => {
  /**
   * Event listener to close modal when user hits escape
   */
  const handleDismiss = (event: any) => {
    const { key } = event;
    if (key === 'Escape') {
      try {
        closeModal();
      } catch {
        throw new Error("Modal close: can't find closeModal() prop");
      }
    }
  };

  const handleClose = (e: any) => {
    const { target } = e;
    if (disableDismiss || !target.classList.contains('cef-modal__backdrop'))
      return;

    try {
      closeModal();
    } catch {
      throw new Error("Modal close: can't find closeModal() prop");
    }
  };

  // disabling background scroll when modal is opened
  useEffect(() => {
    if (isOpen) {
      const backdrop = document.querySelector('.cef-modal__backdrop');
      if (backdrop) trapFocus(backdrop);
      if (!enableBackgroundScroll) document.body.style.overflow = 'hidden';
      if (!disableDismiss) window.addEventListener('keydown', handleDismiss);
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      window.removeEventListener('keydown', handleDismiss);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalBackdrop
      role="dialog"
      className={`${className} cef-modal__backdrop`}
      theme={{ ...config, isOpen }}
      onClick={handleClose}>
      <ModalContainer>{children}</ModalContainer>
    </ModalBackdrop>
  );
};

export default Modal;
